import {ChangeDetectionStrategy, Component, computed, effect, inject, Input, signal} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {MatToolbar} from '@angular/material/toolbar';
import {AuthService} from '../../services/auth-service';
import {RouterLink} from '@angular/router';
import {UserManagementService} from '../../services/user-management-service';
import {Group} from '../../models/api/management/Group';
import {AppLanguage} from '../../i18n/translations';
import {I18nService} from '../../services/i18n-service';
import {AppTheme, ThemeService} from '../../services/theme-service';

@Component({
  selector: 'app-main-menu',
  imports: [
    MatButton,
    MatToolbar,
    RouterLink
  ],
  templateUrl: './main-menu.html',
  styleUrl: './main-menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainMenu {
  protected readonly i18n = inject(I18nService);
  protected readonly themeService = inject(ThemeService);
  protected readonly languages: AppLanguage[] = ['en', 'fr', 'it'];
  protected readonly themes: AppTheme[] = ['light', 'dark', 'ninon'];
  protected readonly currentUser = inject(AuthService).currentUser;
  protected readonly groups = inject(UserManagementService).groups;
  protected readonly groupsLoading = inject(UserManagementService).groupsLoading;
  protected readonly hasAdminAccess = inject(UserManagementService).hasAdminAccess;
  protected readonly selectedGroupId = inject(UserManagementService).selectedGroupId;
  protected readonly selectedGroupName = computed(() => {
    if (this.groupsLoading()) {
      return this.i18n.t('mainMenu.loadingGroups');
    }

    const groups = this.groups();
    if (groups.length === 0) {
      return this.i18n.t('mainMenu.noGroupsAvailable');
    }

    const selectedGroupId = this.selectedGroupId();
    return groups.find(group => group.id === selectedGroupId)?.name ?? groups[0].name;
  });
  protected readonly currentUserLabel = computed(() => {
    const profile = this.userManagementService.currentProfile();
    const fullName = [profile?.firstName, profile?.lastName]
      .filter((value): value is string => !!value && value.trim().length > 0)
      .join(' ');
    if (fullName) {
      return fullName;
    }

    const user = this.currentUser();
    return profile?.email || user?.displayName || user?.email || user?.id || '';
  });
  protected readonly currentUserPictureUrl = computed(() => {
    const pictureUrl = this.currentUser()?.pictureUrl?.trim();
    return pictureUrl ? pictureUrl : null;
  });
  protected readonly currentUserInitials = computed(() => {
    const profile = this.userManagementService.currentProfile();
    const user = this.currentUser();
    const firstName = profile?.firstName?.trim() || user?.givenName?.trim() || null;
    const lastName = profile?.lastName?.trim() || user?.familyName?.trim() || null;

    const initials = [firstName, lastName]
      .filter((value): value is string => !!value)
      .map(value => value.charAt(0).toUpperCase())
      .join('');
    if (initials) {
      return initials.slice(0, 2);
    }

    const displaySource = this.currentUserLabel().trim();
    if (!displaySource) {
      return '?';
    }
    const parts = displaySource.split(/[\s@._-]+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return displaySource.slice(0, 2).toUpperCase();
  });
  protected readonly avatarImageFailed = signal(false);

  @Input() public title = '';

  constructor(
    protected authService: AuthService,
    protected userManagementService: UserManagementService
  ) {
    effect(() => {
      this.currentUserPictureUrl();
      this.avatarImageFailed.set(false);
    });
  }

  protected loginWithGoogle(): void {
    this.authService.login('google');
  }

  protected loginWithMicrosoft(): void {
    this.authService.login('office365');
  }

  protected logout(): void {
    this.authService.logout().subscribe();
  }

  protected onAvatarImageError(): void {
    this.avatarImageFailed.set(true);
  }

  protected closeUserDropdown(dropdown: HTMLDetailsElement): void {
    dropdown.removeAttribute('open');
  }

  protected selectGroup(group: Group, dropdown: HTMLDetailsElement): void {
    this.userManagementService.setSelectedGroup(group.id);
    dropdown.removeAttribute('open');
  }

  protected setLanguage(language: AppLanguage): void {
    this.userManagementService.setLanguagePreference(language);
  }

  protected setTheme(theme: AppTheme): void {
    this.userManagementService.setThemePreference(theme);
  }
}
